import { ReactNode } from 'react'
import { create } from 'zustand'

import { ModalKey } from '@features/modal/types'

export interface ModalConfig {
  title?: ReactNode;
  description?: string;
  hideIconClose?: boolean;
  disabledScroll?: boolean;
  stickyTop?: boolean;
  topOffset?: number;
  backdropTopOffset?: number;
  classes?: {
    modal?: string;
    body?: string;
    content?: string;
  };
  onClose?: () => void;
}

export interface ModalState {
  isOpen: boolean;
  content: ReactNode;
  config?: ModalConfig;
}

export interface ModalStore {
  modals: Map<ModalKey, ModalState>;
  openModal: (
    modalKey: ModalKey,
    content: ReactNode,
    config?: ModalConfig,
  ) => void;
  closeModal: (modalKey: ModalKey, skipOnClose?: boolean) => void;
  closeAllModals: () => void;
  getOpenModalsCount: () => number;
  onClose?: () => void;
}

const useModalStore = create<ModalStore>((set, get) => ({
  modals: new Map(),

  openModal: (modalKey, content, config) =>
    set((state) => {
      const newModals = new Map(state.modals)
      newModals.set(modalKey, { isOpen: true, content, config })
      return { modals: newModals }
    }),

  closeModal: (modalKey, skipOnClose = false) =>
    set((state) => {
      const newModals = new Map(state.modals)
      const modalToClose = newModals.get(modalKey)
      if (modalToClose?.config?.onClose && !skipOnClose) {
        modalToClose.config.onClose()
      }
      if (modalToClose) {
        newModals.set(modalKey, { ...modalToClose, isOpen: false })
      }
      newModals.delete(modalKey)
      return { modals: newModals }
    }),

  closeAllModals: () =>
    set((state) => {
      const newModals = new Map<ModalKey, ModalState>()
      state.modals.forEach((_, key) => {
        newModals.set(key, { isOpen: false, content: null })
      })
      return { modals: newModals }
    }),

  getOpenModalsCount: () => {
    let count = 0
    get().modals.forEach((modal) => {
      if (modal.isOpen) count++
    })
    return count
  },
}))

export default useModalStore
